import { useEffect, useRef, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";

import "./PrivacyPolicy.css";

const sections = [
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Your Information" },
  { id: "sharing", label: "Sharing Your Information" },
  { id: "cookies", label: "Cookies & Storage" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Rights" },
  { id: "data-security", label: "Data Security" },
  { id: "childrens-privacy", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

export function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState("information-we-collect");
  const sectionRefs = useRef({});

  useEffect(() => {
    const scroller = document.querySelector(".legalLayoutRoot");

    const handleScroll = () => {
      const OFFSET = 120;
      let currentSection = sections[0].id;
      for (const { id } of sections) {
        const el = sectionRefs.current[id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= OFFSET) {
            currentSection = id;
          }
        }
      }
      setActiveSection(currentSection);
    };

    const target = scroller || window;
    target.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => target.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const setRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <Box className="legalPageWrap legalPrivacyWrap">
      <Box component="aside" className="legalSidebar">
        <Box component="nav">
          <Box component="ul" className="legalSidebarList">
            {sections.map(({ id, label }) => {
              const isActive = activeSection === id;
              return (
                <Box component="li" key={id} className="legalSidebarItem">
                  <button
                    onClick={() => scrollToSection(id)}
                    className={`legalSidebarBtn ${isActive ? "isActive" : ""}`}
                    type="button"
                  >
                    {label}
                  </button>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box component="main" className="legalMain">
        <Typography className="legalH1" component="h1">
          Privacy Policy
        </Typography>
        <Typography className="legalEffective" component="p">
          Effective: 14 October 2026
        </Typography>

        <Box className="legalInfoBanner legalInfoBanner--blue">
          <Box className="legalInfoBannerStripe legalInfoBannerStripe--blue" />
          <Typography
            className="legalInfoBannerText legalInfoBannerText--blue"
            component="p"
          >
            The Quest is a web platform for employee engagement and career
            development, including a gamified career path and qualification
            steps (the "Service"). The Quest is provided by QCG Sweden AB,
            Engelbrektsgatan 6, 211 33 Malmö, Sweden ("Q Group", "we", "us").
            Sliq Group Private Limited, India, provides engineering, hosting
            administration, and support to Q Group as its service provider.
            Your employer, the organisation that gave you access, is
            the controller of your employee personal data. Q Group processes
            that data on the organisation's instructions.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="information-we-collect"
          ref={setRef("information-we-collect")}
          className="legalSection"
        >
          <SectionTitle number={1} title="Information We Collect" />
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            Your organisation provisions your account. The Quest does not offer
            public self-signup.
          </Typography>
          <SubTitle>Information your organisation and you provide:</SubTitle>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText legalListHasBottomLarge"
          >
            <li>
              Work identity: name, work email, employee ID, and job title.
              Mobile number, location, and date of birth can be left blank.
              Date of birth is a profile field only. It is not used to grant
              access, award points, or decide promotions
            </li>
            <li>A password, stored only as a hash</li>
            <li>An optional profile photo</li>
            <li>
              Activity you create in The Quest, including tasks, courses,
              points, rewards, and promotions
            </li>
            <li>
              Optional feedback you send from the product, including your name,
              email, rating, and comments. That feedback is stored in The
              Quest's database
            </li>
          </Box>
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            The Quest does not collect special-category data such as health,
            biometrics, religion, trade-union membership, political opinions,
            racial or ethnic origin, or sex life. It does not collect national
            ID numbers or salary.
          </Typography>
          <SubTitle>Information collected automatically:</SubTitle>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText"
          >
            <li>
              Security and session logs, including IP address, request URL, and
              time
            </li>
            <li>
              Strictly necessary cookies and local storage used to keep you
              signed in (see Section 04)
            </li>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="how-we-use"
          ref={setRef("how-we-use")}
          className="legalSection"
        >
          <SectionTitle number={2} title="How We Use Your Information" />
          <Typography className="legalBody legalBodyBlack" component="p">
            Q Group uses employee personal data only to provide The Quest to
            your organisation:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText"
          >
            <li>
              To provide the employee engagement and career-path service your
              organisation has set up
            </li>
            <li>
              To send transactional email, such as password resets and
              promotion notices
            </li>
            <li>To respond to support requests and optional product feedback</li>
            <li>To keep the Service secure and investigate misuse</li>
            <li>To meet legal obligations that apply to Q Group</li>
          </Box>
          <Typography className="legalBody legalMutedText legalBodyHasTopSmall" component="p">
            This processing follows your organisation's instructions. It is not
            marketing, and The Quest does not ask you to opt in to promotional
            email. Signing in acknowledges these terms. It is not a separate
            consent toggle for how your employer uses your data.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="sharing"
          ref={setRef("sharing")}
          className="legalSection"
        >
          <SectionTitle number={3} title="Sharing Your Information" />
          <Typography
            className="legalBody legalBodyBlack legalLineTight"
            component="p"
          >
            Q Group does not sell your personal information. Your organisation
            can see the employee data it administers in The Quest. We also share
            data with the suppliers below, and when the law requires it.
          </Typography>
          <Box
            component="ul"
            className="legalList legalListLoose legalMutedText"
          >
            <li>
              <span className="legalEmphasis">Sliq Group:</span> Sliq Group
              Private Limited, India, provides engineering, hosting
              administration, and support to Q Group as its service provider
            </li>
            <li>
              <span className="legalEmphasis">Amazon Web Services:</span>{" "}
              Hosting, email, file storage, logs, and sessions. Hosting and
              file storage are in AWS India
            </li>
            <li>
              <span className="legalEmphasis">Google:</span> Sign-in. Google is
              a Q Group subprocessor because the app sends you to Google
            </li>
            <li>
              <span className="legalEmphasis">Microsoft:</span> Sign-in, when
              your organisation uses it. A Microsoft tenant your organisation
              runs is your organisation's service, not a Q Group subprocessor
            </li>
          </Box>
          <Typography className="legalBody legalMutedText legalBodyHasTopSmall" component="p">
            Personal data is processed in India, including by Sliq Group and in
            AWS India. That processing is outside the EU, the EEA, and the
            UK when you work in those places. Apart from a Microsoft tenant
            your organisation runs, the suppliers named in this section are the
            subprocessors. Material changes to that list are posted in this
            policy before they take effect.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="cookies"
          ref={setRef("cookies")}
          className="legalSection"
        >
          <SectionTitle number={4} title="Cookies & Storage" />
          <Typography className="legalBody legalBodyBlack" component="p">
            The Quest uses strictly necessary cookies and browser storage so you
            can stay signed in. It does not use analytics or marketing cookies.
          </Typography>
          <Box
            component="ul"
            className="legalList legalListLoose legalMutedText legalListHasBottom"
          >
            <li>
              <span className="legalEmphasis">Session cookies:</span> HttpOnly
              cookies named jwt and connect.sid, used for sign-in and the
              sign-in session
            </li>
            <li>
              <span className="legalEmphasis">Local storage:</span> The signed-in
              session, the organisation you have selected, and onboarding-tour
              flags
            </li>
          </Box>
          <Box className="legalInfoBanner legalInfoBanner--yellow">
            <Box className="legalInfoBannerStripe legalInfoBannerStripe--yellow" />
            <Typography
              className="legalInfoBannerText legalInfoBannerText--yellow"
              component="p"
            >
              If you block these cookies or clear this storage, you will not be
              able to stay signed in.
            </Typography>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="data-retention"
          ref={setRef("data-retention")}
          className="legalSection"
        >
          <SectionTitle number={5} title="Data Retention" />
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            Q Group keeps your personal data while you use The Quest and as your
            organisation instructs. An organisation administrator can erase one
            employee's direct identifiers in the database and in stored files.
            That erasure is not a full wipe of backups.
          </Typography>
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            You cannot close your own account in settings. An organisation can
            ask Q Group at{" "}
            <a href="mailto:info@quest-q.com" className="legalLink">
              info@quest-q.com
            </a>{" "}
            for an export of its data, and for deletion when the agreement
            ends. Q Group carries that out on request. The Service has no
            self-service download of an organisation's data and no automatic
            deletion.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Request logs that have left the database follow the configured
            CloudWatch retention. On the Nexer infrastructure the default is 14
            days. Admin activity records stay in the database and have no
            automatic expiry.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="your-rights"
          ref={setRef("your-rights")}
          className="legalSection"
        >
          <SectionTitle number={6} title="Your Rights" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            Access, correction, deletion, portability, restriction, and
            objection are exercised through your employer, the controller. An
            administrator can erase one employee's direct identifiers. Your
            organisation can ask Q Group for an export of its data, and for
            deletion when the agreement ends. The Quest does not offer a
            self-service download. You may also complain to a supervisory
            authority, such as the Swedish Authority for Privacy Protection
            (IMY).
          </Typography>
          <Box
            component="ul"
            className="legalList legalListLoose legalMutedText"
          >
            <li>
              <span className="legalEmphasis">Access and portability:</span> Ask
              your organisation. Your organisation can ask Q Group for an
              export. The Quest does not provide a self-service download of
              your personal data
            </li>
            <li>
              <span className="legalEmphasis">Correction:</span> In Account
              settings you can update your name, date of birth, and mobile
              number. Your email and organisation fields are managed by an
              administrator
            </li>
            <li>
              <span className="legalEmphasis">Deletion:</span> Ask your
              organisation. An administrator can request erasure. There is no
              delete button on your own account
            </li>
          </Box>
          <Typography
            className="legalBody legalMutedText legalBodyHasTopSmall"
            component="p"
          >
            Points and promotions are not decisions made solely by automated
            means. Questions about this policy can be sent to{" "}
            <a href="mailto:info@quest-q.com" className="legalLink">
              info@quest-q.com
            </a>
            . Q Group will refer employee-data requests to your organisation.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="data-security"
          ref={setRef("data-security")}
          className="legalSection"
        >
          <SectionTitle number={7} title="Data Security" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            Q Group protects personal information with access controls and
            encryption in transit (TLS). Passwords are stored as hashes.
          </Typography>
          <Typography className="legalBody legalMutedText legalBodyHasBottomSmall" component="p">
            If Q Group becomes aware of a personal data breach affecting an
            organisation's data, it notifies that organisation without undue
            delay.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            No method of transmission over the internet or electronic storage is
            completely secure. Q Group cannot guarantee absolute security.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="childrens-privacy"
          ref={setRef("childrens-privacy")}
          className="legalSection"
        >
          <SectionTitle number={8} title="Children's Privacy" />
          <Typography className="legalBody legalMutedText" component="p">
            The Quest is for employees of customer organisations. It is not
            directed to anyone under 16. If you believe a child's data has been
            added, contact{" "}
            <a href="mailto:info@quest-q.com" className="legalLink">
              info@quest-q.com
            </a>{" "}
            and your organisation.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="changes"
          ref={setRef("changes")}
          className="legalSection"
        >
          <SectionTitle number={9} title="Changes to This Policy" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            Q Group may update this Privacy Policy when the Service or the law
            changes. Material changes will be posted in The Quest before they
            take effect.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Continued use of The Quest after the effective date means you have
            seen the updated policy. Your organisation remains the controller of
            employee personal data.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        <Box
          component="section"
          id="contact"
          ref={setRef("contact")}
          className="legalSection legalSectionLast"
        >
          <SectionTitle number={10} title="Contact Us" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottom"
            component="p"
          >
            Questions about this Privacy Policy:
          </Typography>
          <Box className="legalContact legalMutedText">
            <Typography component="p" className="legalBody">
              <span className="legalEmphasis">Email:</span>{" "}
              <a href="mailto:info@quest-q.com" className="legalLink">
                info@quest-q.com
              </a>
            </Typography>
            <Typography component="p" className="legalBody legalContactCompany">
              <span className="legalEmphasis">Provider:</span> QCG Sweden AB,
              Engelbrektsgatan 6, 211 33 Malmö, Sweden
            </Typography>
            <Typography component="p" className="legalBody legalContactCompany">
              <span className="legalEmphasis">Service provider:</span> Sliq
              Group Private Limited, India
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SectionTitle({ number, title }) {
  return (
    <Typography component="h2" className="legalSectionTitle">
      {number}. {title}
    </Typography>
  );
}

function SubTitle({ children }) {
  return (
    <Typography component="p" className="legalSubTitle">
      {children}
    </Typography>
  );
}
